import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { licenseAPI, pluginAPI } from '../utils/api';

const PERIOD_OPTIONS = [1, 90, 180, 360];

const LicenseForm = ({ mode, initialValues, onSubmit, onCancel, submitting }) => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userId, setUserId] = useState(initialValues?.user?._id || initialValues?.user || '');
  const [periodDays, setPeriodDays] = useState(initialValues?.periodDays || '');
  const [terminalId, setTerminalId] = useState(initialValues?.terminalId || '');
  const [accountNumber, setAccountNumber] = useState(initialValues?.accountNumber || '');
  const [plugins, setPlugins] = useState([]);
  const [loadingPlugins, setLoadingPlugins] = useState(true);
  const [pluginId, setPluginId] = useState(initialValues?.plugin?._id || initialValues?.plugin || '');

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await licenseAPI.getActiveUsers();
        setUsers(response.data.data || []);
      } catch (error) {
        toast.error(t('errors.serverError'));
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchActiveUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchActivePlugins = async () => {
      try {
        const response = await pluginAPI.list({ limit: 100 });
        setPlugins(response.data.data || []);
      } catch (error) {
        toast.error(t('errors.serverError'));
      } finally {
        setLoadingPlugins(false);
      }
    };
    fetchActivePlugins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentPluginId = initialValues?.plugin?._id || initialValues?.plugin;
    if (currentPluginId && !plugins.find((p) => p._id === currentPluginId)) {
      const deactivatedPlugin = initialValues?.plugin;
      if (deactivatedPlugin && deactivatedPlugin.name) {
        setPlugins((prev) => [
          ...prev,
          { ...deactivatedPlugin, _id: currentPluginId, isActive: false },
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugins, initialValues]);

  const isEdit = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      toast.error(t('licences.form.userRequired'));
      return;
    }
    if (!periodDays) {
      toast.error(t('licences.form.periodRequired'));
      return;
    }
    if (!pluginId) {
      toast.error(t('licences.form.pluginRequired'));
      return;
    }

    const data = { user: userId, plugin: pluginId, periodDays: Number(periodDays) };

    if (isEdit) {
      data.terminalId = terminalId || null;
      data.accountNumber = accountNumber || null;
    }

    try {
      await onSubmit(data);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(t('licences.form.userHasActiveLicense'));
      } else if (error.response?.status === 400) {
        toast.error(t('licences.errors.createFailed'));
      } else {
        toast.error(t('errors.serverError'));
      }
    }
  };

  const canSubmit = !loadingUsers && !loadingPlugins && userId && pluginId && periodDays && !submitting;

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
      <h2 className="text-lg font-bold text-gray-900">
        {isEdit ? t('licences.form.editTitle') : t('licences.form.title')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-1">
            {t('licences.form.user')} *
          </label>
          <select
            id="user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="form-input w-full"
            disabled={loadingUsers || submitting}
            required
          >
            <option value="">{t('licences.form.userPlaceholder')}</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.displayId ? `${u.displayId} — ` : ''}
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="period" className="block text-sm font-medium text-gray-700 mb-1">
            {t('licences.form.period')} *
          </label>
          <select
            id="period"
            value={periodDays}
            onChange={(e) => setPeriodDays(e.target.value)}
            className="form-input w-full"
            disabled={submitting}
            required
          >
            <option value="">{t('licences.form.periodPlaceholder')}</option>
            {PERIOD_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {t(`licences.period.${p}days`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="plugin" className="block text-sm font-medium text-gray-700 mb-1">
            {t('licences.form.plugin')} *
          </label>
          <select
            id="plugin"
            value={pluginId}
            onChange={(e) => setPluginId(e.target.value)}
            className="form-input w-full"
            disabled={loadingPlugins || submitting}
            required
          >
            <option value="">{t('licences.form.pluginPlaceholder')}</option>
            {plugins.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
                {!p.isActive && ` (${t('licences.status.inactive')})`}
              </option>
            ))}
          </select>
        </div>

        {isEdit && (
          <>
            <div>
              <label htmlFor="terminalId" className="block text-sm font-medium text-gray-700 mb-1">
                {t('licences.form.terminalId')}
              </label>
              <input
                id="terminalId"
                type="text"
                value={terminalId}
                onChange={(e) => setTerminalId(e.target.value)}
                maxLength={200}
                className="form-input w-full"
                disabled={submitting}
              />
              <p className="text-xs text-gray-500 mt-1">{t('licences.form.terminalIdHint')}</p>
            </div>

            <div>
              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                {t('licences.form.accountNumber')}
              </label>
              <input
                id="accountNumber"
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                maxLength={100}
                className="form-input w-full"
                disabled={submitting}
              />
              <p className="text-xs text-gray-500 mt-1">{t('licences.form.accountNumberHint')}</p>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="btn-secondary px-4 py-2 disabled:opacity-50"
        >
          {t('licences.form.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEdit ? t('licences.form.saveChanges') : t('licences.form.generate')}
        </button>
      </div>
    </form>
  );
};

export default LicenseForm;